import { AsyncOperationsModel } from "../../../utility/AsyncOperationsModel";
import { observable, action, computed } from "mobx";
import { IUser, AccountStatus } from "../../../model/ModelTypes";
import BaseComponent from "../../../utility/BaseComponent";
import { observer } from "mobx-react";
import * as React from "react";
import { withRouter, BrowserHistory } from "react-router-dom";
import { isNullOrUndefined } from "util";
import { Snackbar, TextField, CircularProgress, Card, Button, SelectField, Paper } from "react-md";
import StyledTable from "../../../component/StyledTable/StyledTable";
import "./static/UsersTableView.scss";
import VerifyActionDialog, { VerifyActionDialogModel } from "../../../component/VerifyActionDialog/VerifyActionDialog";
import _ = require("lodash");
import BulkEditUsers, { BulkEditUsersModel } from "../../../component/BulkEditUsers/BulkEditUsers";
import { EditUsersResponse, EditUserItem } from "../../../utility/admin/AdminApi";
import AddUserDialog, { AddUserDialogModel } from "../../../component/AddUserDialog/AddUserDialog";
import { IServicesProvider } from "../../../di/IServicesProvider";
import * as moment from "moment";

export class UsersTableViewModel extends AsyncOperationsModel {

    public readonly tableHeaders: string[] = ["Username", "Name", "Email", "Role", "Group", "Admin", "Status"];
    @observable users: IUser[];
    @observable services: IServicesProvider;
    @observable visibleUsers: IUser[] = [];
    @observable searchQuery: string;
    @observable selectedUsers: IUser[] = [];
    @observable showDeleteDialog: boolean = false;
    @observable showApproveDialog: boolean = false;
    @observable showBulkEditDialog: boolean = false;
    @observable toasts: string[] = [];
    @observable organisationCode: string;
    @observable showAddUserDialog: boolean = false;
    @observable addUserDialogModel: AddUserDialogModel;
    @observable filterKey: string = "";
    @observable filterValue: string = "";

    @computed get requiresAttentionCount() {
        return this.users.filter(item => item.status === 0).length;
    }

    @computed get tableData() {
        return this.visibleUsers.map(this.prepareData);
    }

    @computed get availabeRoles() {
        let allroles = ["All"];
        this.users
            .filter(item => !isNullOrUndefined(item.role) && !isNullOrUndefined(item.role.name))
            .forEach(
                item => {
                    if (allroles.indexOf(item.role.name) < 0) {
                        allroles.push(item.role.name);
                    }
                }
            );
        return allroles;
    }

    @computed get filterKeys() {
        let keys = [];
        if (this.availabeRoles.length > 0) {
            keys.push("Role");
        }
        keys.push("Status");
        return keys;
    }

    @computed get filterValues() {
        if (this.filterKey === "Role") {
            return this.availabeRoles;
        } else if (this.filterKey === "Status") {
            return ["All", "Approved", "Requires Attention"];
        } else {
            return [];
        }
    }

    @action.bound
    onFilterValueSelected(value: string) {
        if (value === "All") {
            this.visibleUsers = this.users;
        } else {
            if (this.filterKey === "Status") {
                try {
                    let statusValue = 1;
                    if (value === "Requires Attention") {
                        statusValue = 0;
                    } else {
                        statusValue = 1;
                    }
                    this.visibleUsers = this.users.filter(
                        item => item.status === statusValue
                    );
                } catch (error) {
                    this.setErrorMessages(["Failed to filter by status"]);
                }
            } else if (this.filterKey === "Role") {
                this.visibleUsers = this.users
                    .filter(item => !isNullOrUndefined(item.role)
                        && !isNullOrUndefined(item.role.name)
                        && item.role.name === value
                    );
            }

        }
        this.filterValue = value;
    }

    constructor(services: IServicesProvider, organisationCode?: string) {
        super();
        this.services = services;
        if (!isNullOrUndefined(organisationCode)) {
            this.organisationCode = organisationCode;
        }
        this.addUserDialogModel = new AddUserDialogModel(this.services,
                                                         this.organisationCode,
                                                         this.onAddUserComplete);
    }

    @action.bound
    onBulkEditCompleted(result?: EditUsersResponse, requests?: EditUserItem[]) {
        if (!isNullOrUndefined(result) && !isNullOrUndefined(requests)) {
            // this.prepare();
            this.setToast("Accounts Updated Successfully");
        }
        this.clearSelectedUsers();
        this.showBulkEditDialog = false;
    }

    @action.bound
    onApproveUsersExecuted(withError: boolean) {
        if (!withError) {
            // this.prepare();
            this.setToast("Approve Executed Successfully");
        }
        this.clearSelectedUsers();

        this.showApproveDialog = false;

    }

    @action.bound
    onDeleteUsersExecuted(withError: boolean) {
        if (!withError) {
            // this.prepare();
            this.setToast("Delete Executed Successfully");
        }
        this.clearSelectedUsers();
        this.showDeleteDialog = false;

    }

    @action.bound setUsers(users: IUser[]) {
        this.users = users;
        this.visibleUsers = users;
        this.searchQuery = "";
        this.selectedUsers = [];
    }

    @action.bound prepare() {
        this.incrementAsyncOperations();
        this.services.getAdminServicesProvider().getUsersControls().getOperations().observeUsers(false)
            .subscribe(
                result => {
                    if (this.asyncOperationsCount > 0) {
                        this.decrementAsyncOperations();
                    }
                    this.setUsers(result);
                },
                error => {
                    throw error;
                }
            );
    }

    @action.bound
    onRowsSelected(rows: Array<Array<string>>) {
        rows.forEach(
            selectedRow => {
                let username = selectedRow[0];
                let selectedUserIndex = this.users.findIndex(
                    (user) => {
                        if (!isNullOrUndefined(user.username)) {
                            return user.username.replace(`${user.organisation}-`, "") === username;
                        }
                        return false;
                    }
                );
                if (selectedUserIndex > -1) {
                    let selectedUser = this.users[selectedUserIndex];
                    let indexInSelectedUsers = this.selectedUsers.findIndex(
                        item => item.id === selectedUser.id
                    );
                    if (indexInSelectedUsers > -1) {
                        this.selectedUsers.splice(indexInSelectedUsers, 1);
                    } else {
                        this.selectedUsers.push(selectedUser);
                    }
                }
            }
        );
    }

    @action.bound
    clearSelectedUsers() {
        this.selectedUsers = [];
    }

    @action.bound
    approveUserClicked(row: Array<string>) {
        this.onRowsSelected([row]);
        this.showApproveDialog = true;
    }

    @action.bound
    editUserClicked(selectedRow: Array<string>) {
        let username = selectedRow[0];
        let selectedUserIndex = this.users.findIndex(
            (user) => {
                if (!isNullOrUndefined(user.username)) {
                    return user.username.replace(`${user.organisation}-`, "") === username;
                }
                return false;
            }
        );
        if (selectedUserIndex > -1) {
            let selectedUser = this.users[selectedUserIndex];
            this.addUserDialogModel.setUser(selectedUser);
            this.showAddUserDialog = true;
        }
    }

    @action.bound
    deleteUserClicked(row: Array<string>) {
        this.onRowsSelected([row]);
        this.showDeleteDialog = true;
    }

    @action.bound
    setToast(message: string) {
        this.dismissToast();
        this.toasts.push(message);
    }

    @action.bound
    dismissToast() {
        this.toasts.splice(0, this.toasts.length);
    }

    @action
    onSearchQueryChange(query: string) {
        this.searchQuery = query;
        this.visibleUsers =
            this.users.filter(item => (!isNullOrUndefined(item.username) && item.username.includes(query)) ||
                                                  (!isNullOrUndefined(item.name) && item.name.includes(query)));
    }

    @action.bound
    onAddUserComplete(withError: boolean) {
        this.addUserDialogModel.setUser(undefined);
        this.showAddUserDialog = false;
        if (!withError) {
            this.setToast("Operation Executed Successfully");
        }
        if (this.selectedUsers.length === 1) {
            this.clearSelectedUsers();
        }
    }

    private prepareData(user: IUser): Array<string | undefined> {
        let userProps: Array<string | undefined> = [];
        if (!isNullOrUndefined(user.username) && !isNullOrUndefined(user.organisation)) {
            userProps.push(user.username.replace(
                `${user.organisation.toUpperCase()}-`, ""));
        } else {
            userProps.push("");
        }
        userProps.push(user.name);
        userProps.push(user.email);
        if (!isNullOrUndefined(user.role)) {
            userProps.push(user.role.name);
        } else {
            userProps.push("");
        }
        if (!isNullOrUndefined(user.groups)) {
            let userGroup = "";
            user.groups.forEach(group => userGroup += ` / ${group.name}`);
            userGroup = userGroup.slice(2, userGroup.length);
            userProps.push(userGroup);
        } else {
            userProps.push("");
        }
        if (!isNullOrUndefined(user.is_admin)) {
            userProps.push(
                user.is_admin ? "Yes" : "No"
            );
        } else {
            userProps.push("");
        }
        switch (user.status) {
            case AccountStatus.approved:
                userProps.push("Approved");
                break;
            case AccountStatus.PendingAdminApproval:
                userProps.push("Requires Attention");
                break;
            case AccountStatus.PendingEmailVerification:
                userProps.push("Awaiting Verification");
                break;
            default:
                userProps.push("");
        }
        return userProps;
    }
}

@observer
class UsersTableView extends BaseComponent<{ model: UsersTableViewModel, history: BrowserHistory }, {}> {
    dialogModel: VerifyActionDialogModel<IUser> =
        new VerifyActionDialogModel();

    bulkEditModel: BulkEditUsersModel =
        new BulkEditUsersModel(
            this.props.model.services.getAdminServicesProvider().getUsersControls().getOperations(),
            this.props.model.organisationCode,
            this.props.model.onBulkEditCompleted);

    getAccountStatus(status?: AccountStatus): string {
        switch (status) {
            default:
                return "";
        }
    }

    render() {
        this.dialogModel.setItems(this.props.model.selectedUsers);
        this.bulkEditModel.setUsers(this.props.model.selectedUsers);
        this.bulkEditModel.setShowDialog(this.props.model.showBulkEditDialog);
        this.props.model.addUserDialogModel.setShowDialog(this.props.model.showAddUserDialog);
        return (
            <div className="UsersTableContainer">
                {
                    !isNullOrUndefined(this.props.model.users) &&
                    this.props.model.users.length > 0 &&
                    <Paper
                        zDepth={1}
                        className="UsersRequiresAttentionMessage"
                    >
                        You have {this.props.model.requiresAttentionCount} accounts requiring attention
                    </Paper>
                }

                {
                    isNullOrUndefined(this.props.model.users) ||
                    this.props.model.users.length === 0 &&
                    <CircularProgress id="progress" centered={true}/>
                }
                {
                    !isNullOrUndefined(this.props.model.users) &&
                    this.props.model.users.length > 0 &&
                    <Card>
                        <div className="UsersTableHeader">
                            <TextField
                                style={{width: "200px"}}
                                id="usernameSearch"
                                label="Search"
                                lineDirection="center"
                                placeholder="Search By Name/Username"
                                className="md-cell md-cell--bottom"
                                value={this.props.model.searchQuery}
                                onChange={(e) => this.props.model.onSearchQueryChange(e.toString())}

                            />
                            <div className="UsersTableHeaderFilters md-cell md-cell--bottom">
                                <SelectField
                                    style={{width: "80px"}}
                                    id="filter-key-selector"
                                    label="Filter By"
                                    placeholder="Select A Filter"
                                    menuItems={this.props.model.filterKeys}
                                    value={this.props.model.filterKey}
                                    onChange={(value) => this.props.model.filterKey = value.toString()}
                                />
                                {
                                    this.props.model.filterValues.length > 0 &&
                                    <SelectField
                                        style={{width: "130px", marginLeft: "10px"}}
                                        id="filter-value-selector"
                                        label="Value"
                                        placeholder="Select A Value"
                                        menuItems={this.props.model.filterValues}
                                        value={this.props.model.filterValue}
                                        onChange={(value) => {
                                            this.props.model.onFilterValueSelected(value.toString());
                                        }}
                                    />
                                }
                            </div>
                            <div className="UsersTableHeaderActions">
                                <Button
                                    id="csv-export"
                                    secondary={true}
                                    flat={true}
                                    onClick={() => {
                                        this.props.model.services
                                            .getAdminServicesProvider()
                                            .getUsersControls()
                                            .getOperations()
                                            .getApi()
                                            .exportTableDataAsCSV(this.props.model.tableHeaders,
                                                                  this.props.model.tableData)
                                            .subscribe(
                                                downloadedFile => {

                                                    let element = document.createElement("a");
                                                    element.href = URL.createObjectURL(downloadedFile);
                                                    element.download =
                                                        `users-${moment().toDate().toLocaleDateString()}.csv`;
                                                    element.click();
                                                }
                                            );
                                    }}
                                >
                                    Export Users
                                </Button>
                                <Button
                                    flat={true}
                                    secondary={true}
                                    onClick={() => {
                                        this.props.model.addUserDialogModel.setUser(undefined);
                                        this.props.model.showAddUserDialog = true;
                                    }}
                                > Add Account
                                </Button>
                                <Button
                                    flat={true}
                                    secondary={true}
                                    style={{display: "none"}}
                                >
                                    CSV Import
                                </Button>
                            </div>
                            {
                                !isNullOrUndefined(this.props.model.selectedUsers) &&
                                this.props.model.selectedUsers.length > 0 &&
                                <div className="UsersTableHeaderActions UsersTableHeaderSelectedActions">
                                    <Button
                                        flat={true}
                                        secondary={true}
                                        onClick={() => this.props.model.showBulkEditDialog = true}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        flat={true}
                                        secondary={true}
                                        onClick={() => this.props.model.showApproveDialog = true}
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        flat={true}
                                        onClick={() => this.props.model.showDeleteDialog = true}
                                        style={{color: "red"}}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            }
                        </div>
                        <StyledTable
                            selectable={true}
                            withActions={true}
                            onItemSelected={this.props.model.onRowsSelected}
                            headers={[
                                "Username", "Name", "Email", "Role", "Group", "Admin", "Status"
                            ]}
                            data={this.props.model.tableData}
                            actions={[
                                {
                                    label: "Approve",
                                    onClick: this.props.model.approveUserClicked
                                },
                                {
                                    label: "Edit",
                                    onClick: this.props.model.editUserClicked
                                },
                                {
                                    label: "Delete",
                                    color: "red",
                                    onClick: this.props.model.deleteUserClicked
                                }
                            ]}
                            clearSelected={this.props.model.clearSelectedUsers}
                        />
                        <VerifyActionDialog
                            title="Confirm Delete"
                            model={this.dialogModel}
                            showDialog={this.props.model.showDeleteDialog}
                            body={`Are you sure you want to delete ${this.props.model.selectedUsers.length} account
                        ${this.props.model.selectedUsers.length > 1 ? "s" : ""} ?`}
                            operation={this.props.model.services
                                .getAdminServicesProvider().getUsersControls().getOperations()
                                .deleteUsers(this.props.model.selectedUsers)}
                            tag="DELETE"
                            onCompleteFunction={this.props.model.onDeleteUsersExecuted}
                        />
                        <VerifyActionDialog
                            title="Confirm Approve"
                            model={this.dialogModel}
                            showDialog={this.props.model.showApproveDialog}
                            body={`Are you sure you want to approve ${this.props.model.selectedUsers.length} account
                        ${this.props.model.selectedUsers.length > 1 ? "s" : ""} ?`}
                            operation={this.props.model.services
                                .getAdminServicesProvider().getUsersControls().getOperations()
                                .editUsers(this.props.model.selectedUsers.map(item => {
                                    let user: IUser = {accessToken: "", refreshToken: ""};
                                    _.assign(user, item);
                                    user.status = AccountStatus.approved;
                                    return user;
                                }))}
                            tag="APPROVE"
                            onCompleteFunction={this.props.model.onApproveUsersExecuted}
                        />
                        <BulkEditUsers
                            model={this.bulkEditModel}
                        />
                        <AddUserDialog model={this.props.model.addUserDialogModel}/>
                        <Snackbar
                            id="example-snackbar"
                            toasts={this.props.model.toasts.map(item => {
                                return {
                                    text: item
                                };
                            })}
                            autohide={true}
                            onDismiss={this.props.model.dismissToast}
                        />
                    </Card>
                }

            </div>
        );
    }
}

export default withRouter(UsersTableView);


// WEBPACK FOOTER //
// ./src/view/Admin/UsersTableView/UsersTableView.tsx